import { ButtonDeleteRow, ButtonIcon, Table } from "mta-rkvr-styles";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { get, post, remove } from "../api/fetch";
import { ColumnHeader, RealEstateMessage } from "../types/homepage/RealEstateComplexMessages";
import { RealEstateComplexResponseBody } from "../api/HomePageResponseTypes";
import { REALESTATEUNIT } from "../util/paths";
import { toLocaleDate } from "../util/conversionUtils";
import { Paginator } from "../types/util/TableFunctionalityTypes";
import MessagesDetailModal from "../realEstateUnits/realEstateUnit/MessagesDetailModal";
import CurrentUserContext from "../CurrentUserContext";
import { InfotextQueryBody, InfotextResponse } from "../api/InfobuttonTextsTypes";

const RealEstateComplexMessages = () => {
  const { t } = useTranslation();
  const tBase = "homePageAuth.realEstateComplexMessages.";

  const { infobuttonTexts, updateInfotexts } = useContext(CurrentUserContext);

  useEffect(() => {
    if (infobuttonTexts.loadedComplex) {
      return;
    }
    post<InfotextQueryBody, InfotextResponse>({
      endpoint: "infotexts",
      requestBody: {
        onTekst: true,
        vaated: [
          "kvyOtsing",
          "kvyAndmed",
          "kvyAndmedUus",
          "kvyDokumendidMuutmine",
          "kvyKinnitamiseksEdastamine",
          "kvyTaiendamiseleSaatmine",
          "kvyLopetamine",
          "kvyAegrida",
          "kvyAegridaCSV",
          "kvyVaraMuutmine",
          "kvyVaraOtsing",
          "kvyNaitajadMuutmine",
          "kvyNaitajadLisamine",
          "kvyToimingud",
          "kvyTeated",
          "kvyTeatedDetailid",
          "kvyUus",
          "kvyMassmuutmine",
          "aadressiotsing",
        ],
      },
    }).then((resp) => {
      if (resp.ok) {
        updateInfotexts("COMPLEX", resp.body.resultsList);
      } else {
        console.log("Failed to load infotexts");
      }
    });
  }, []);

  const [messages, setMessages] = useState<RealEstateMessage[]>();
  const [refreshObservable, setRefreshObservable] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const refresh = () => {
    setRefreshObservable(!refreshObservable);
  };
  const [paginator, setPaginator] = useState<Paginator>();
  const onPagination = (paginator: { pageSize: number; elementOffset: number }) => {
    getMessages(paginator.pageSize, paginator.elementOffset);
  };
  useEffect(() => {
    getMessages();
  }, [refreshObservable]);

  const [isDetailView, setDetailView] = useState<string | undefined>();
  const openDetailView = (id: number) => {
    setDetailView(id.toString());
  };

  const closeDetailView = () => {
    setDetailView(undefined);
  };

  const getMessages = (pageSize?: number, elementOffset?: number) => {
    if (isLoading) {
      return;
    }
    setLoading(true);
    get<RealEstateComplexResponseBody>({
      endPoint: "realEstateComplexHomePageMessages",
      queryParams: [
        { name: "limit", value: pageSize ? pageSize : 10 },
        { name: "offset", value: elementOffset ? elementOffset : 0 },
      ],
    }).then(async ({ ok, body }) => {
      if (ok) {
        setMessages(
          body.resultsList.map((message) => {
            return {
              ...message,
              saadetud: toLocaleDate(message.saadetud),
              vastatud: toLocaleDate(message.vastatud),
            };
          }),
        );
        setLoading(false);
        setPaginator({
          pageSize: body.limit,
          elementOffset: body.offset,
          numberOfElements: body.resultCount,
          totalElements: body.totalCount,
        });
      } else {
        console.error("Failed to load KVY messages. ");
      }
    });
  };

  const markDone = (id: number) => {
    remove<never>({ endpoint: "realEstateUnitMessageDetail", pathParams: [id] }).then((res) => {
      res.ok && refresh();
    });
  };
  if (messages == undefined) {
    return (
      <Table
        id={t(tBase + "title")}
        title={t(tBase + "title")}
        headers={Object.keys(ColumnHeader).map((header) => {
          return t(tBase + header);
        })}
        content={[]}
        translate={t}
        loading
      />
    );
  }

  return (
    <>
      {messages && (
        <>
          <Table
            id={t(tBase + "title")}
            title={t(tBase + "title")}
            headers={Object.keys(ColumnHeader).map((header) => {
              return t(tBase + header);
            })}
            paginator={paginator}
            onPagination={onPagination}
            loading={isLoading}
            content={messages.map((message) => {
              const { saadetud, vastatud, vastus, sisu, kommentaar, kvyKood, kvyId } = message;
              return {
                menetluseNumber: <Link to={`${REALESTATEUNIT}/${kvyId}`}>{kvyKood}</Link>,
                saadetud,
                sisu,
                kommentaar,
                vastatud,
                vastus,
              };
            })}
            translate={t}
            wrapBigColumns
            subTables={{
              headers: [t(tBase + "comment"), t(tBase + "reply")],
              contents: messages.map((message) => {
                return [{ sisu1: message.kommentaar, teateSisu: message.vastus }];
              }),
              togglers: ["kommentaar", "vastus"],
            }}
            rowIds={messages.map((message) => message.id)}
            editable
            customEditRowComponent={(obj: { rowId: number }) => (
              <>
                <ButtonIcon
                  iconName={"icon-open"}
                  onClick={() => {
                    openDetailView(obj.rowId);
                  }}
                />
                <ButtonDeleteRow
                  onConfirm={() => {
                    markDone(obj.rowId);
                  }}
                  confirmationText={t(`${tBase}confirmRemove`)}
                />
              </>
            )}
          />
          {isDetailView && (
            <MessagesDetailModal
              isOpen={isDetailView != undefined}
              close={closeDetailView}
              messageId={isDetailView}
              refreshMessages={refresh}
              infobuttonTexts={infobuttonTexts}
            />
          )}
        </>
      )}
    </>
  );
};

export default RealEstateComplexMessages;
